import { Injectable } from '@angular/core';
import { IssuecardComponent } from '../components/issuecard/issuecard.component';
import { IssuecardHelper } from '../components/issuecard/issuecard.helper';
import { SyncEventType } from '../models/enums/syncEventType';
import { EventIssuecardClosed } from '../models/syncEvents/realtimeEvents/eventIssuecardClosed';
import { EventIssuecardMoved } from '../models/syncEvents/realtimeEvents/eventIssuecardMoved';
import { EventIssuecardUpdated } from '../models/syncEvents/realtimeEvents/eventIssuecardUpdated';
import { EventIssuecardUpdatedExt } from '../models/syncEvents/realtimeEvents/eventIssuecardUpdatedExt';
import { DynamicElementService } from '../services/dynamicElement.service';
import { SynchronizationService } from '../services/synchronization.service';
import { HTMLElementPositioningHelper } from '../util/htmlelement-positioning-helper';
import { BaseBoardComponent } from './baseBoard';
import {EventUserLoggedOutPers} from '../models/syncEvents/persistenceEvents/eventUserLoggedOut';
import {TaskboardViewComponent} from './taskboard-view/taskboard-view.component';
import {EventIssueToForeground} from '../models/syncEvents/persistenceEvents/eventIssueToForeground';
import {OnlineuserService} from '../services/onlineuser.service';

/**
 * This class is encapsulating the handling of synchronsation-events shared by all boards/actionviews
 */
@Injectable({
  providedIn: 'root',
  })
export class ActionViewSynchroHelper {
  constructor(
    private syncService: SynchronizationService,
    private positionHelper: HTMLElementPositioningHelper,
    private dynamicElementService: DynamicElementService,
    private issuecardHelper: IssuecardHelper,
    private onlineUserService: OnlineuserService
  ) { }

  public initSyncEventListeners(board: BaseBoardComponent) {
    this.initIssuecardMovedSubscription(board);
    this.initCloseIssuecardSubscription(board);
    this.initUpdateIssueCardSubscription(board);
    this.initUpdateIssueCardExtendedDataSubscription(board);
    this.initUserLoggedOutSubscription(board);
    this.initUserLoggedin(board);
  }
  private initUserLoggedOutSubscription(board: BaseBoardComponent) {
    board.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.USERLOGGEDOUT).subscribe(payload => {
      const event = EventUserLoggedOutPers.fromPayload(payload);
      console.log(event);
      if (board instanceof TaskboardViewComponent){
        board.getAllIssueComponent().forEach(card => card.removeAvatar(event.avatar));
      }
      this.dynamicElementService.getAllIssueCards().forEach(card => card.removeAvatar(event.avatar));
    }));
  }

  private initUserLoggedin(board: BaseBoardComponent) {
    board.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.USER_LOGGEDIN).subscribe(_ => {
      // send message only if cards are available
      if (this.dynamicElementService.getAllIssueCards().length > 0) {
        this.syncService.sendPersistenceEventNew(new EventIssueToForeground(this.dynamicElementService.getAllIssueCards()[0].issue.id,
          this.onlineUserService.getAvatarUrl()));
      }
    }));
  }

  private initIssuecardMovedSubscription(board: BaseBoardComponent) {
    board.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUECARD_MOVED).subscribe(payload => {
      const event = EventIssuecardMoved.fromPayload(payload);
      this.dynamicElementService.getAllIssueCards().forEach(card => card.removeAvatar(event.avatar));
      const card = this.dynamicElementService.getIssuecardForIssue(event.issueId);
      if (ActionViewSynchroHelper.cardExists(card, event.type)) {
        card.updateAvatare(event.avatar);
        const cardElement = card.getElementRef().nativeElement;
        this.positionHelper.setRelativePosition(cardElement, event.position.left, event.position.top);
      }
    }));
  }

  private initCloseIssuecardSubscription(board: BaseBoardComponent) {
    board.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUECARD_CLOSED).subscribe(payload => {
      const event = EventIssuecardClosed.fromPayload(payload);
      const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.issueId);
      if (ActionViewSynchroHelper.cardExists(card, event.type)) {
        this.issuecardHelper.removeCardFromGlobalReferences(card);
      }
      if (this.dynamicElementService.getAllIssueCards().length === 0) {
        return;
      }
      const newFocusedIssue = this.dynamicElementService.getAllIssueCards()[this.dynamicElementService.getAllIssueCards().length - 1];
      newFocusedIssue.updateAvatare(event.avatar);
      if (board instanceof TaskboardViewComponent){
        board.getIssueComponent(newFocusedIssue.issue.id).updateAvatare(event.avatar);
      }
      this.dynamicElementService.getAllIssueCards()[this.dynamicElementService.getAllIssueCards().length - 1].updateAvatare(event.avatar);
    }));
  }

  private initUpdateIssueCardSubscription(board: BaseBoardComponent) {
    board.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUECARD_UPDATED).subscribe(payload => {
      const event = EventIssuecardUpdated.fromPayload(payload);
      this.dynamicElementService.getAllIssueCards().forEach(card => card.removeAvatar(event.avatar));
      const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.issueId);
      if (ActionViewSynchroHelper.cardExists(card, event.type)) {
        card.updateAvatare(event.avatar);
        card.update(event.updatedIssue);
      }
    }));
  }

  private initUpdateIssueCardExtendedDataSubscription(board: BaseBoardComponent) {
    board.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.ISSUECARD_UPDATED_EXT).subscribe(payload => {
      const event = EventIssuecardUpdatedExt.fromPayload(payload);
      this.dynamicElementService.getAllIssueCards().forEach(card => card.removeAvatar(event.avatar));
      const card: IssuecardComponent = this.dynamicElementService.getIssuecardForIssue(event.issueId);
      if (ActionViewSynchroHelper.cardExists(card, event.type)) {
        card.updateAvatare(event.avatar);
        card.updateExtendedData();
      }
    }));
  }

  /**
   * Check card exists and display error (in console) if not
   */
  private static cardExists(card: IssuecardComponent, eventType: SyncEventType): boolean {
    if (!card) {
      console.error(eventType + ': card not found.');
      return false;
    }
    return true;
  }


}
